import React, { useCallback, useEffect, useRef, useState } from 'react';

import Loading from 'components/Common/Loading/Loading';
import useCart from 'hooks/useCart';
import { apiUrls } from 'services/api-urls';
import {
  getIframeActionName,
  getIframeUri,
  getIframeOriginUri,
} from 'services/sips';
import { ENV_API_KEY_SYMFONY } from 'settings/env';
import { MethodProps } from 'types/PaymentFormType';

import styles from './Iframe.module.scss';

type Props = Pick<MethodProps, 'setPaymentMethod'> & { paymentMethod: string };

export default function Iframe({
  setPaymentMethod,
  paymentMethod,
}: Props): JSX.Element {
  const { cart } = useCart();
  const [iframeUri, setIframeUri] = useState<string | undefined>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadIframeUri = useCallback(async () => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    await setPaymentMethod(paymentMethod, false);

    const response = await fetch(apiUrls.getOrderValidation(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-AUTH-TOKEN': ENV_API_KEY_SYMFONY,
      },
      signal: abortController.signal,
    });

    if (!response.ok) {
      return Promise.reject('Failed to validate the order');
    }

    const data = await response.json();

    if (!data.sips) {
      return Promise.reject('Error during loading the payment iframe');
    }

    cart && localStorage.setItem('order', JSON.stringify(cart));
    setIframeUri(getIframeUri(data.evtId));
  }, []);

  // Proceed to redirect the client to response route after receive an action from iframe
  const messageListener = useCallback((event: MessageEvent) => {
    if (event.origin !== getIframeOriginUri()) return;

    if (event.data.action === getIframeActionName()) {
      const { target, inputs } = event.data.data;

      const fakeForm = document.createElement('form');
      fakeForm.method = 'POST';
      fakeForm.action = target;

      for (const key in inputs) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = inputs[key];

        fakeForm.append(input);
      }

      document.body.append(fakeForm);
      fakeForm.submit();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', messageListener);

    loadIframeUri().catch(e => {
      if (e.name === 'AbortError') {
        return;
      }

      console.error(e);
    });

    return () => {
      window.removeEventListener('message', messageListener);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (!iframeUri) {
    return (
      <div className={styles.container}>
        <Loading
          loading
          disablePortal
          className={styles.loadingBackdrop}
          disableBodyScrollbar={false}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <iframe src={iframeUri} />
    </div>
  );
}
